import { BookOpen } from "lucide-react";
import { Reveal } from "./Reveal";

const PORTRAITS = {
  dinesh: "/images/dr-dinesh-kumar-singh.jpg",
  vimla: "/images/vimla-chendriah.png",
  kirti: "/images/dr-kirti.jpg",
};

const TEAM = [
  {
    id: "dinesh",
    name: "Dr Dineshkumar Singh",
    role: "Programme Lead · Ayurvedic Physician & Wellness Consultant",
    img: PORTRAITS.dinesh,
    bio: "Dr. Dineshkumar Singh blends classical Ayurveda with modern preventive science to deliver truly personalized care. His programs focus on stress management, sleep optimization, and lifestyle-based wellness. With international experience in luxury wellness hospitality, he brings clinical depth and a refined, individual-first approach. His mission: to enhance your healthspan, vitality, and quality of life.",
  },
  {
    id: "kirti",
    name: "Dr Kirti Malviya",
    role: "Ayurvedic Doctor",
    img: PORTRAITS.kirti,
    bio: "An Ayurvedic doctor devoted to authentic, classical Panchakarma care. Dr Kirti Malviya assesses each guest's unique constitution and imbalances, designs personalised treatment protocols, and monitors every stage of the cleanse — bringing gentle, precise clinical care to your healing journey.",
  },
];

const BOOKS = [
  { title: "30 Days to a Healthier and Happier You — Ancient Wisdom for Modern Living", tag: "Author" },
  { title: "Living a More Thoughtful Life", tag: "Co-Author" },
];

export const Team = () => (
  <section
    id="practitioners"
    data-testid="team-section"
    className="border-t border-border/50 py-28 lg:py-40"
  >
    <div className="mx-auto max-w-[1400px] px-6 lg:px-12">
      <Reveal>
        <p
          data-testid="team-overline"
          className="text-[11px] font-medium uppercase tracking-[0.4em] text-accent"
        >
          Our Practitioners
        </p>
      </Reveal>
      <Reveal delay={0.1}>
        <h2
          data-testid="team-heading"
          className="mt-8 max-w-3xl font-serif text-4xl font-light leading-[1.05] tracking-tight sm:text-5xl lg:text-6xl"
        >
          Held by <em className="text-accent">expert hands</em>.
        </h2>
      </Reveal>

      <div className="mt-20 grid gap-12 lg:grid-cols-12 lg:gap-16">
        <Reveal className="lg:col-span-4">
          <div className="group overflow-hidden border border-border/60">
            <img
              src={PORTRAITS.vimla}
              alt="Vimla Chendriah, Programme Lead"
              className="aspect-[3/4] w-full object-cover transition-transform duration-[1200ms] ease-out group-hover:scale-105"
            />
          </div>
        </Reveal>
        <div className="lg:col-span-8">
          <Reveal delay={0.1}>
            <p className="text-[11px] font-medium uppercase tracking-[0.3em] text-accent">
              Programme Lead · Author · Speaker
            </p>
            <h3
              data-testid="team-vimla-name"
              className="mt-4 font-serif text-3xl font-normal sm:text-4xl lg:text-5xl"
            >
              Vimla Chendriah
            </h3>
          </Reveal>
          <Reveal delay={0.2}>
            <div data-testid="team-vimla-bio" className="mt-8 max-w-2xl space-y-5 text-sm font-light leading-relaxed text-muted-foreground lg:text-base">
              <p>
                Vimla is a published author, speaker, and Ayurvedic, mindfulness and mental health
                practitioner, passionate about bringing the ancient wisdom of Ayurveda into
                modern-day living. In a period of stress and burnout, she discovered the power of
                Ayurveda and the transformative impact of Panchakarma on her own health and
                wellbeing.
              </p>
              <p>
                She has delivered a wide range of wellbeing trainings and workshops across many
                organisations, and continues her mission of empowering people to use simple,
                sustainable and natural solutions for better health — travelling regularly to India
                for her own retreats and to deepen her knowledge of Ayurveda.
              </p>
              <p>
                Having lived in various countries, her international journey has shaped her
                understanding of people, cultures and wellbeing. Bilingual in English and French,
                she naturally bridges cultures and perspectives, bringing a warm, inclusive and
                globally minded approach to her work.
              </p>
            </div>
          </Reveal>
          <Reveal delay={0.3}>
            <div className="mt-10 space-y-4">
              {BOOKS.map((b) => (
                <div key={b.title} className="flex items-start gap-4 border-l border-accent/40 pl-5">
                  <BookOpen size={16} className="mt-1 shrink-0 text-accent" />
                  <div>
                    <p className="font-serif text-lg italic leading-snug text-foreground/90">{b.title}</p>
                    <p className="mt-1 text-[10px] font-medium uppercase tracking-[0.25em] text-muted-foreground">
                      {b.tag}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </Reveal>
        </div>
      </div>

      <div className="mt-20 grid max-w-5xl gap-20">
        {TEAM.map((member, i) => (
          <Reveal key={member.id} delay={i * 0.12}>
            <div
              data-testid={`team-card-${member.id}`}
              className="group grid items-center gap-8 sm:grid-cols-12 lg:gap-12"
            >
              <div className="overflow-hidden border border-border/60 sm:col-span-5">
                <img
                  src={member.img}
                  alt={`${member.name}, ${member.role}`}
                  className="aspect-[3/4] w-full object-cover transition-transform duration-[1200ms] ease-out group-hover:scale-105"
                />
              </div>
              <div className="sm:col-span-7">
                <h4 className="font-serif text-2xl font-normal sm:text-3xl">{member.name}</h4>
                <p className="mt-3 text-[11px] font-medium uppercase tracking-[0.3em] text-accent">
                  {member.role}
                </p>
                {member.bio && (
                  <p className="mt-5 max-w-xl text-sm font-light leading-relaxed text-muted-foreground lg:text-base">
                    {member.bio}
                  </p>
                )}
              </div>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);
